import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Terminal, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function formatErr(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Access granted");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md glass neon-border rounded-lg p-8 fade-up"
      >
        <div className="w-12 h-12 rounded-md bg-[#00ff66]/10 border border-[#00ff66]/40 flex items-center justify-center mb-5 glow-pulse">
          <Lock size={22} className="text-[#00ff66]" />
        </div>
        <h1 className="font-mono text-2xl font-extrabold">Admin Access</h1>
        <p className="font-mono text-xs text-slate-500 mt-1 mb-6 cursor-blink">$ sudo login</p>

        <label className="font-mono text-xs uppercase tracking-widest text-slate-500">email</label>
        <Input
          data-testid="admin-username-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@exploitvault.io"
          className="mt-1 mb-4 font-mono text-sm bg-[#0b111d] border-slate-800 text-slate-100"
          required
        />

        <label className="font-mono text-xs uppercase tracking-widest text-slate-500">password</label>
        <Input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1 mb-6 font-mono text-sm bg-[#0b111d] border-slate-800 text-slate-100"
          required
        />

        <Button
          data-testid="admin-login-submit-button"
          type="submit"
          disabled={loading}
          className="w-full bg-[#00ff66] hover:bg-[#00e65c] text-[#04120a] font-mono font-bold"
        >
          {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Terminal size={16} className="mr-2" />}
          Authenticate
        </Button>
      </form>
    </div>
  );
}
